import React, { useContext, useState, useEffect } from "react";
import {
  Box,
  Typography,
  useTheme,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import backgroundImage from "../assets/dashboard/background.png";
import axios from "axios";

import { getColors } from "../themes/theme";
import { ThemeContext } from "../context/ThemeContext";
import { useAuthContext } from "../hooks/contexts/useAuthContext";

const Dashboard = () => {
  const { token, user, updateTutorialStatus } = useAuthContext();
  const { signOut } = useAuthContext();
  const { darkMode } = useContext(ThemeContext);
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:600px)"); // Check if screen width is less than 768px

  const [openAcceptDialog, setOpenAcceptDialog] = useState(false);
  const [openSubscribeDialog, setOpenSubscribeDialog] = useState(false);
  const [openTutorialDialog, setOpenTutorialDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (user?.licenseAcceptance === 0) {
      setOpenAcceptDialog(true);
    }
    // Check if tutorial should be shown
    if (user?.licenseAcceptance === 1 && user?.tutorialStatus === "pending") {
      setOpenTutorialDialog(true);
    }
    // if (user?.subscribed === 0) {
    //   setOpenSubscribeDialog(true);
    // }
    const shouldShowTrialModal = sessionStorage.getItem("showTrialModal");
    if (shouldShowTrialModal === "true") {
      setOpenSubscribeDialog(true);
      sessionStorage.removeItem("showTrialModal");
    }
  }, [user]);

  const handleAccept = async () => {
    setOpenAcceptDialog(false);
    setLoading(true);
    await axios.put(
      `${process.env.REACT_APP_API_URL}/practice/${user?.practiceId}`,
      {
        goodlic: 1,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    window.location.reload();
  };

  const handleReject = async () => {
    await signOut();
  };

  const handleVideoPlay = async () => {
    try {
      await updateTutorialStatus("watched");
    } catch (error) {
      console.error("Error updating tutorial status:", error);
    }
  };

  const handleDismissTutorial = async () => {
    try {
      await updateTutorialStatus("dismissed");
      setOpenTutorialDialog(false);
    } catch (error) {
      console.error("Error updating tutorial status:", error);
    }
  };

  const handleWatchLater = () => {
    setOpenTutorialDialog(false);
  };

  return (
    <>
      {loading ? (
        <Box
          display={"flex"}
          justifyContent="center"
          alignItems="center"
          height="100vh"
        >
          <CircularProgress size={50} />
        </Box>
      ) : (
        <Box
          display={openAcceptDialog ? "none" : "flex"}
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          flexGrow={1}
          sx={{ mx: { xs: "10px", md: 4 } }}
        >
          <Typography
            sx={{
              fontSize: { md: "24px", xs: "18px" },
              fontWeight: 500,
              color: theme.palette.text.secondary,
              textAlign: "center",
            }}
          >
            Welcome to 3DPractice!
          </Typography>
          <img
            src={backgroundImage}
            alt="Doctor Illustration"
            width={isMobile ? 250 : 400}
          />
          <Typography
            sx={{
              fontSize: { md: "24px", xs: "18px" },
              color: theme.palette.text.secondary,
              textAlign: "center",
            }}
          >
            Select an option from the left menu or a module from the top bar to
            get started.
          </Typography>
        </Box>
      )}
      <Dialog open={openAcceptDialog} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            fontSize: { xs: "24px", sm: "32px" },
            fontWeight: 600,
            color: darkMode
              ? getColors.drawerTextDark
              : getColors.thumbnailTextLight,
          }}
        >
          License Acceptance
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              fontSize: { xs: "18px", sm: "26px" },
              fontWeight: 500,
              color: "#1F82BE",
              mb: { xs: 2, sm: 4 },
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            onClick={() => {
              window.open("/license.pdf", "_blank");
            }}
          >
            Display License
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            sx={{ cursor: "pointer" }}
            onClick={() => {
              window.open("http://get.adobe.com/reader/", "_blank");
            }}
          >
            <img
              src={
                "https://img.icons8.com/?size=100&id=meFi4bu3VWU5&format=png&color=000000"
              }
              alt="Adobe License"
              width="40px"
            />
            <Typography
              sx={{
                fontSize: { xs: "16px", sm: "20px" },
                fontWeight: 400,
                color: "#1F82BE",
                ml: 1,
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              Adobe Reader
            </Typography>
          </Box>
          <Typography
            sx={{
              my: { xs: 2, sm: 4 },
              fontSize: { xs: "16px", sm: "20px" },
              fontWeight: 600,
              color: darkMode
                ? getColors.drawerTextDark
                : getColors.thumbnailTextLight,
            }}
          >
            You must read and accept the license agreement to complete the first
            login process. If the license aggreement does not appear, be sure
            that you allow a pop-up window to open and that you have Adobe
            reader installed. You can use the link the download and install the
            Adobe reader software.
          </Typography>
          <DialogActions sx={{ gap: { xs: 1, sm: 2 }, margin: 0, padding: 0 }}>
            <Button
              variant="contained"
              onClick={handleReject}
              sx={{
                fontSize: { xs: "14px", sm: "18px" },
                fontWeight: 500,
                fontFamily: "Poppins",
                px: 4,
                py: 1,
                borderRadius: "5px",
              }}
            >
              Reject
            </Button>
            <Button
              variant="contained"
              onClick={handleAccept}
              sx={{
                fontSize: { xs: "14px", sm: "18px" },
                fontWeight: 500,
                fontFamily: "Poppins",
                px: 4,
                py: 1,
                borderRadius: "5px",
              }}
            >
              Accept
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
      <Dialog
        open={openSubscribeDialog && user?.licenseAcceptance === 1}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <Typography
            sx={{
              mb: { xs: 1, sm: 2 },
              fontSize: { xs: "24px", sm: "24px" },
              fontWeight: 600,
              color: darkMode
                ? getColors.drawerTextDark
                : getColors.thumbnailTextLight,
            }}
          >
            {user?.remainingDays === 0
              ? "Your trial will expire in 24 hours."
              : `Your trial will expire in ${user?.remainingDays} days.`}
          </Typography>

          <Typography
            sx={{
              mb: { xs: 2, sm: 4 },
              fontSize: { xs: "20px", sm: "20px" },
              fontWeight: 500,
              color: darkMode
                ? getColors.drawerTextDark
                : getColors.thumbnailTextLight,
            }}
          >
            Please select a subscription plan to continue using the 3DPractice
            services.
          </Typography>

          <Box sx={{ gap: { xs: 1, sm: 2 }, margin: 0, padding: 0 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                setOpenSubscribeDialog(false);
              }}
              sx={{
                fontSize: { xs: "14px", sm: "18px" },
                fontWeight: 500,
                fontFamily: "Poppins",
                px: 4,
                py: 1,
                borderRadius: "5px",
                mb: 1,
              }}
            >
              Continue to Trial
            </Button>
            <Typography
              sx={{
                fontSize: { md: "24px", xs: "18px" },
                fontWeight: 500,
                color: theme.palette.text.secondary,
                textAlign: "center",
                mb: 1,
              }}
            >
              or
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                window.open(
                  "https://buy.stripe.com/3cI28q37JdhV6Ungdu2Nq0G",
                  "_blank"
                );
              }}
              sx={{
                fontSize: { xs: "14px", sm: "18px" },
                fontWeight: 500,
                fontFamily: "Poppins",
                px: 4,
                py: 1,
                borderRadius: "5px",
                mb: 2,
              }}
            >
              Monthly Subscription
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                window.open(
                  "https://buy.stripe.com/bJe6oG7nZ2DhfqTbXe2Nq0H",
                  "_blank"
                );
              }}
              sx={{
                fontSize: { xs: "14px", sm: "18px" },
                fontWeight: 500,
                fontFamily: "Poppins",
                px: 4,
                py: 1,
                borderRadius: "5px",
              }}
            >
              Yearly Subscription
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog
        open={openTutorialDialog}
        onClose={handleWatchLater}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontSize: { xs: "20px", sm: "24px" },
            fontWeight: 600,
            color: darkMode
              ? getColors.drawerTextDark
              : getColors.thumbnailTextLight,
            textAlign: "center",
            pb: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            position: "relative",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, justifyContent: "center" }}>
            <InfoIcon sx={{ color: "#1976d2", fontSize: { xs: "24px", sm: "28px" } }} />
            Welcome to 3DPractice!
          </Box>
          <IconButton
            onClick={handleWatchLater}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: darkMode ? getColors.drawerTextDark : getColors.thumbnailTextLight,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              mb: { xs: 2, sm: 3 },
              fontSize: { xs: "16px", sm: "18px" },
              fontWeight: 500,
              color: darkMode
                ? getColors.drawerTextDark
                : getColors.thumbnailTextLight,
              textAlign: "center",
            }}
          >
            Getting Started Tutorial
          </Typography>
          
          <video 
            width="100%" 
            height="315" 
            onContextMenu={(e) => e.preventDefault()} 
            controlsList="nodownload" 
            controls 
            src="https://d2nut6gc5qzbiv.cloudfront.net/37504600/TutorialVideos/getting-started.mp4"
            onPlay={handleVideoPlay}
          />
          
          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleWatchLater}
              sx={{
                fontSize: { xs: "14px", sm: "16px" },
                fontWeight: 500,
                fontFamily: "Poppins",
                px: 3,
                py: 1,
                borderRadius: "5px",
              }}
            >
              Watch Later
            </Button>
            <Button
              fullWidth
              variant="text"
              onClick={handleDismissTutorial}
              sx={{
                fontSize: { xs: "14px", sm: "16px" },
                fontWeight: 500,
                fontFamily: "Poppins",
                px: 3,
                py: 1,
                borderRadius: "5px",
                color: theme.palette.text.secondary,
              }}
            >
              Dismiss
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Dashboard;
